import type { ChangeEvent } from "react";
import { Button, Form, Input, Popconfirm } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { FormAlternativeUI } from "../utils";
import { useLessonForm } from "./LessonFormContext";
import AlternativePills from "./AlternativePills";
import BlockItem from "./BlockItem";

const { TextArea } = Input;

interface AlternativeCardProps {
  alternative: FormAlternativeUI;
  activityIndex: number;
  alternativeIndex: number;
}

/**
 * SCRUM-795: una actividad alternativa sin manipulativos.
 *
 * Es la misma captura que una actividad normal menos el toggle de
 * manipulativos: título, tiempo, pastillas pedagógicas, resumen y bloques. Los
 * bloques son los mismos `BlockItem` que el resto del formulario, en su modo
 * anidado.
 */
const AlternativeCard: React.FC<AlternativeCardProps> = ({
  alternative,
  activityIndex,
  alternativeIndex,
}) => {
  const {
    handleAlternativeUpdate,
    addAlternativeBlock,
    removeAlternativeBlock,
    removeAlternative,
  } = useLessonForm();

  return (
    <div className="activity-section alternative-section">
      <div className="activity-header-with-actions">
        <h4>Actividad alternativa {alternativeIndex + 1}</h4>
        <div className="activity-actions">
          <Popconfirm
            title="¿Estás seguro de eliminar esta actividad alternativa?"
            onConfirm={() => removeAlternative(activityIndex, alternativeIndex)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              className="delete-alternative-button"
            />
          </Popconfirm>
        </div>
      </div>

      <div className="activity-header">
        <Form.Item label="Título actividad" className="activity-title">
          <Input
            placeholder="Título"
            value={alternative.title}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleAlternativeUpdate(
                activityIndex,
                alternativeIndex,
                "title",
                e.target.value,
              )
            }
          />
        </Form.Item>

        <Form.Item
          label="Tiempo de actividad (en minutos)"
          className="activity-duration"
        >
          <Input
            type="number"
            placeholder="Minutos"
            value={alternative.duration}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleAlternativeUpdate(
                activityIndex,
                alternativeIndex,
                "duration",
                Number(e.target.value),
              )
            }
          />
        </Form.Item>
      </div>

      <AlternativePills
        activityIndex={activityIndex}
        alternativeIndex={alternativeIndex}
        alternative={alternative}
      />

      <div className="activity-summary-container">
        <Form.Item
          label="Resumen de actividad"
          className="activity-summary"
          extra="Máximo 150 caracteres. Este resumen será visible para los profesores."
        >
          <TextArea
            placeholder="Resumen breve de la actividad..."
            value={alternative.summary || ""}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleAlternativeUpdate(
                activityIndex,
                alternativeIndex,
                "summary",
                e.target.value,
              )
            }
            maxLength={150}
            showCount
            rows={3}
          />
        </Form.Item>
      </div>

      {alternative.blocks.map((block, blockIndex) => (
        <BlockItem
          key={block.id ?? blockIndex}
          block={block}
          blockIndex={blockIndex}
          activityIndex={activityIndex}
          alternativeIndex={alternativeIndex}
          totalBlocks={alternative.blocks.length}
          onRemove={() =>
            removeAlternativeBlock(activityIndex, alternativeIndex, blockIndex)
          }
        />
      ))}

      <Button
        type="dashed"
        onClick={() => addAlternativeBlock(activityIndex, alternativeIndex)}
        icon={<PlusOutlined />}
        className="add-block-button"
      >
        Añadir bloque
      </Button>
    </div>
  );
};

export default AlternativeCard;
